/**
 * Roadside vegetation placement (issue #44): occasional PATCHES (not
 * continuous) of mixed trees, shrubs, and ground cover, offset to the verge
 * of the OSM tarmac-road network (data/config/roads.json) — the same
 * road/route geometry roadWalls.ts extrudes drystone walls along. Pure
 * three.js-free placement maths, unit-tested in Node; vegetation.ts wraps the
 * output in instanced three.js geometry and grounds it via
 * TerrainCollider.sampleGround, mirroring RoadWallsLayer / BuildingsLayer.
 *
 * Purely illustrative scene furniture: vegetation has NO bearing on any LOS
 * result (hard constraint #1, guarded by vegetation.guard.test.ts). Placement
 * is a stylised approximation (patches assumed near tarmac verges; not a
 * surveyed ecological inventory) and elevations are never invented — every
 * instance is later dropped onto the DTM via the same collider sampleGround
 * that backs walk-mode ground lock (hard constraint #6).
 *
 * Determinism: every random-looking choice (patch boundaries, per-slot
 * keep/skip, kind, variant, scale, rotation) comes from the same seeded
 * hash01 idiom as roadWalls.ts / buildings.ts / turbines.ts (Math.sin-based),
 * so a given roads.json + options produces byte-identical planting on every
 * load — never Math.random(). densify()/perpendiculars() are re-exported from
 * roadWalls.ts so both verge-offset layers share one implementation.
 */
import { densify, hash01, perpendiculars, roadLocalLine } from './roadWalls';
import { bngToLocal, type LocalXZ } from '../core/coordinates';
import type { LandcoverClass } from '../core/manifest';
import type { Road, RoadsFile } from '../core/manifest';
import { clipToCorridor, distanceToPolylineM } from './corridor';
import type { TarmacClearance } from './tarmacClearance';

export type VegetationKind = 'tree' | 'shrub' | 'groundcover';

export const TREE_VARIANTS = 3;
export const SHRUB_VARIANTS = 2;
export const GROUNDCOVER_VARIANTS = 2;

/** Overall card size range per kind (metres) — the "varied size" part of the
 * mixed-species requirement. */
export const TREE_HEIGHT_RANGE_M: [number, number] = [3, 7];
export const SHRUB_HEIGHT_RANGE_M: [number, number] = [0.6, 1.4];
export const GROUNDCOVER_HEIGHT_RANGE_M: [number, number] = [0.15, 0.35];

/** Perpendicular offset range from the road centreline (metres). Every kind's
 * MINIMUM sits just outside the carriageway clearance zone (issue #79:
 * tertiary/unclassified clearance is 2.5 m half-width + 1 m margin = 3.5 m) so
 * verge planting stays snug against the carriageway edge without landing on it
 * — candidates the clearance predicate would drop anyway are simply not
 * generated, keeping verges populated rather than bare. Trees/shrubs reach out
 * further, overlapping the drystone wall's verge line (roadWalls' VERGE_OFFSET_M
 * is 3 m) so foliage occasionally reads as standing in front of a wall — the
 * alpha-tested cutout card then depth-sorts correctly against it (no blending,
 * so no sorting artefacts). Ground cover stays closest in, near the road edge. */
export const TREE_OFFSET_RANGE_M: [number, number] = [3.6, 8];
export const SHRUB_OFFSET_RANGE_M: [number, number] = [3.6, 6.5];
export const GROUNDCOVER_OFFSET_RANGE_M: [number, number] = [3.6, 5.5];

/** Road classes planted by default — the actual lanes, not service roads
 * (driveways / farm access / parking aisles), mirroring roadWalls.ts. */
export const DEFAULT_VEGETATION_CLASSES: Road['road_class'][] = [
  'primary',
  'secondary',
  'tertiary',
  'unclassified',
];

/** Spacing the road centreline is resampled to before placing candidate
 * planting slots (metres) — finer than roadWalls' 8 m so patches read as
 * clusters of several plants rather than one every ring. */
export const VEG_DENSIFY_M = 3;

/** Only roads within this distance of the route corridor are planted at all
 * (mirrors roadWalls' WALL_CORRIDOR_M): roadside vegetation is a near-route
 * visual only. */
export const DEFAULT_VEG_CORRIDOR_M = 250;
/** Ground cover is small (tens of cm) and only reads up close; it is kept to
 * a much tighter radius of the route than trees/shrubs ("near camera",
 * approximated statically via distance-to-route since the camera travels the
 * route in fly-through mode — no per-frame distance check needed). */
export const DEFAULT_GROUNDCOVER_CORRIDOR_M = 60;

/** Average combined patch+gap cycle length (metres); jittered per road/side
 * so neighbouring verges don't repeat in lockstep. */
const PATCH_PERIOD_M = 40;
const PATCH_FRACTION_MIN = 0.25;
const PATCH_FRACTION_MAX = 0.55;
/** Probability a candidate slot inside an active patch actually gets a plant
 * (keeps patches looking loose/clumpy rather than solid), scaled by
 * densityScale for the low quality tier. */
const BASE_KEEP_PROB = 0.55;

export interface VegetationInstance {
  /** Local scene x/z (metres), already offset off the road centreline. */
  x: number;
  z: number;
  kind: VegetationKind;
  /** Palette index within this kind (species/form look) — see *_VARIANTS. */
  variant: number;
  /** Overall card height (metres) — see *_HEIGHT_RANGE_M. */
  scale: number;
  /** Card yaw (radians) for orientation variety. */
  rotationY: number;
}

export interface VegetationOptions {
  wallClasses?: Road['road_class'][];
  /** Route polyline (local coords); roads beyond corridorMaxDistM of it are
   * dropped entirely. Omit to plant every qualifying road unfiltered. */
  corridor?: LocalXZ[];
  corridorMaxDistM?: number;
  /** Tighter radius applied only to ground-cover instances (see
   * DEFAULT_GROUNDCOVER_CORRIDOR_M). Only meaningful when corridor is set. */
  groundCoverCorridorMaxDistM?: number;
  /** Scales the per-slot keep probability, i.e. overall planting density
   * (issue #17 low quality tier: fewer instances). Default 1. */
  densityScale?: number;
  /** When true, no ground-cover instances are produced at all — trees/shrubs
   * (billboard cross-plane cards) only (issue #17 low quality tier). */
  billboardOnly?: boolean;
  /** Carriageway clearance (issue #79): when set, any candidate too close to
   * the route ribbon or a nearby tarmac road centreline is dropped so nothing
   * plants on a road. Pure per-candidate filter — does not alter seeds. */
  clearance?: TarmacClearance;
}

/** Deterministic patch/gap decision at along-run distance distM for one
 * (roadSeed, side) verge: a jittered period + fraction (so patches vary in
 * length and spacing) with a per-cycle phase jitter (so a patch never starts
 * exactly at the cycle boundary — the planting doesn't look metronomic). */
function patchActiveAt(roadSeed: number, side: number, distM: number): boolean {
  const period = PATCH_PERIOD_M * (0.7 + hash01(roadSeed * 13.1 + side * 3.7) * 0.6);
  const fraction =
    PATCH_FRACTION_MIN + hash01(roadSeed * 17.3 + side * 5.9) * (PATCH_FRACTION_MAX - PATCH_FRACTION_MIN);
  const cycleIndex = Math.floor(distM / period);
  const phaseJitter = hash01(roadSeed * 23.7 + side * 7.1 + cycleIndex * 11.3) * period * 0.3;
  const posInCycle = distM - cycleIndex * period;
  return posInCycle >= phaseJitter && posInCycle < phaseJitter + fraction * period;
}

/** Weighted kind pick for a planting slot. Trees are the rarest (tallest,
 * most visually prominent — real roadside patches are shrub/groundcover
 * heavy with occasional trees). billboardOnly (low quality tier) never picks
 * ground cover. */
function pickKind(slotSeed: number, billboardOnly: boolean): VegetationKind {
  const r = hash01(slotSeed * 4.7);
  if (billboardOnly) {
    return r < 0.4 ? 'tree' : 'shrub';
  }
  if (r < 0.18) return 'tree';
  if (r < 0.55) return 'shrub';
  return 'groundcover';
}

function variantCount(kind: VegetationKind): number {
  if (kind === 'tree') return TREE_VARIANTS;
  if (kind === 'shrub') return SHRUB_VARIANTS;
  return GROUNDCOVER_VARIANTS;
}

function heightRange(kind: VegetationKind): [number, number] {
  if (kind === 'tree') return TREE_HEIGHT_RANGE_M;
  if (kind === 'shrub') return SHRUB_HEIGHT_RANGE_M;
  return GROUNDCOVER_HEIGHT_RANGE_M;
}

function offsetRange(kind: VegetationKind): [number, number] {
  if (kind === 'tree') return TREE_OFFSET_RANGE_M;
  if (kind === 'shrub') return SHRUB_OFFSET_RANGE_M;
  return GROUNDCOVER_OFFSET_RANGE_M;
}

const TWO_PI = Math.PI * 2;

/**
 * Builds the deterministic set of vegetation instances for a roads file:
 * patches (not a continuous strip) of mixed trees/shrubs/ground cover offset
 * to either verge of each qualifying, in-corridor road. Positions are in
 * local scene x/z only — grounding (the y elevation) is resolved later by
 * vegetation.ts against the DTM collider, never fabricated here.
 */
export function buildVegetationInstances(
  roadsFile: RoadsFile,
  options: VegetationOptions = {},
): VegetationInstance[] {
  const wallClasses = new Set(options.wallClasses ?? DEFAULT_VEGETATION_CLASSES);
  const corridorMaxDistM = options.corridorMaxDistM ?? DEFAULT_VEG_CORRIDOR_M;
  const groundCoverCorridorMaxDistM = options.groundCoverCorridorMaxDistM ?? DEFAULT_GROUNDCOVER_CORRIDOR_M;
  const densityScale = options.densityScale ?? 1;
  const billboardOnly = options.billboardOnly ?? false;
  const keepProb = BASE_KEEP_PROB * densityScale;

  const instances: VegetationInstance[] = [];
  let roadIdx = 0;

  for (const road of roadsFile.roads) {
    if (!wallClasses.has(road.road_class)) continue;
    const centre = densify(roadLocalLine(road.polyline_bng), VEG_DENSIFY_M);
    if (centre.length < 2) continue;
    const runs = options.corridor ? clipToCorridor(centre, options.corridor, corridorMaxDistM) : [centre];

    for (const run of runs) {
      const perp = perpendiculars(run);
      const roadSeed = roadIdx + 1;
      roadIdx += 1;
      let dist = 0;

      for (let i = 0; i < run.length; i++) {
        if (i > 0) dist += Math.hypot(run[i].x - run[i - 1].x, run[i].z - run[i - 1].z);

        for (const side of [1, -1] as const) {
          const sideIdx = side === 1 ? 0 : 1;
          if (!patchActiveAt(roadSeed, sideIdx, dist)) continue;

          const slotSeed = roadSeed * 9973 + sideIdx * 5003 + i * 131 + 1;
          if (hash01(slotSeed * 2.1) >= keepProb) continue; // sparse within an active patch

          const kind = pickKind(slotSeed, billboardOnly);

          const [offMin, offMax] = offsetRange(kind);
          const offset = offMin + hash01(slotSeed * 3.3) * (offMax - offMin);
          const point: LocalXZ = {
            x: run[i].x + perp[i].x * offset * side,
            z: run[i].z + perp[i].z * offset * side,
          };

          if (kind === 'groundcover' && options.corridor) {
            if (distanceToPolylineM(point, options.corridor) > groundCoverCorridorMaxDistM) continue;
          }

          // Carriageway keep-off (issue #79): drop candidates on the route ribbon
          // or any nearby tarmac road centreline (incl. a parallel/joining road,
          // not just this one). Pure filter after the seed is fixed.
          if (options.clearance && !options.clearance.isClear(point)) continue;

          const [hMin, hMax] = heightRange(kind);
          const scale = hMin + hash01(slotSeed * 6.1) * (hMax - hMin);
          const variant = Math.floor(hash01(slotSeed * 8.9) * variantCount(kind));
          const rotationY = hash01(slotSeed * 12.3) * TWO_PI;

          instances.push({ x: point.x, z: point.z, kind, variant, scale, rotationY });
        }
      }
    }
  }

  return instances;
}

export type CardMode = 'single' | 'cross';

/** Atlas UV rect baked onto a card's geometry (see vegetationTexture.ts). */
export interface CardUvRect {
  u0: number;
  v0: number;
  u1: number;
  v1: number;
}

export interface CardGeometryArrays {
  positions: number[];
  normals: number[];
  uvs: number[];
  indices: number[];
}

/** How far each card vertex normal is blended from the plane's true face
 * normal toward straight up (+Y), in [0, 1]. A cross card's face normals
 * point horizontally, so under an overhead/angled sun the card lit strictly
 * by its face normal shades edge-on to near-black (issue #57's "black
 * lollipop" trees) regardless of how the atlas texture is painted. Blending
 * toward +Y makes the card catch top-down sunlight the way a real canopy's
 * many leaf facets collectively do, without needing per-leaf geometry. */
const UPWARD_NORMAL_BLEND = 0.55;

/** Blends a face normal toward +Y by UPWARD_NORMAL_BLEND and re-normalizes. */
function blendNormalUpward(normal: [number, number, number]): [number, number, number] {
  const [nx, ny, nz] = normal;
  const bx = nx * (1 - UPWARD_NORMAL_BLEND);
  const by = ny * (1 - UPWARD_NORMAL_BLEND) + UPWARD_NORMAL_BLEND;
  const bz = nz * (1 - UPWARD_NORMAL_BLEND);
  const len = Math.hypot(bx, by, bz) || 1;
  return [bx / len, by / len, bz / len];
}

/**
 * Builds one alpha-tested foliage card in local unit space: base at y=0, top
 * at y=1, centred on the vertical axis (x in [-0.5, 0.5] for the first
 * plane). 'single' is one flat quad (cheap — used for ground cover, which is
 * only ever rendered close to the camera); 'cross' adds a second quad rotated
 * 90 degrees about Y (width along Z instead of X) so the card reads from any
 * horizontal viewing angle without per-frame billboarding — used for
 * trees/shrubs, which must stay legible at range along the whole route.
 *
 * The given UV rect (one atlas cell) is baked onto every vertex so every
 * instance built from this geometry samples the same shared atlas texture
 * (vegetationTexture.ts) — no per-instance texture, no per-frame UV writes.
 * Vertex normals are blended toward +Y (see UPWARD_NORMAL_BLEND / issue #57)
 * rather than using the plane's true face normal, so lighting responds to
 * the sun instead of shading the card near-black edge-on.
 * Pure and three.js-free: vegetation.ts wraps the result in a BufferGeometry.
 */
export function buildCardGeometry(mode: CardMode, uv: CardUvRect): CardGeometryArrays {
  const positions: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];

  function addQuad(
    corners: [number, number, number][],
    normal: [number, number, number],
  ): void {
    const base = positions.length / 3;
    for (const [x, y, z] of corners) positions.push(x, y, z);
    const [bx, by, bz] = blendNormalUpward(normal);
    for (let k = 0; k < 4; k++) normals.push(bx, by, bz);
    uvs.push(uv.u0, uv.v0, uv.u1, uv.v0, uv.u1, uv.v1, uv.u0, uv.v1);
    indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
  }

  // Plane A: width along X, at Z=0. Corners in BL, BR, TR, TL order.
  addQuad(
    [
      [-0.5, 0, 0],
      [0.5, 0, 0],
      [0.5, 1, 0],
      [-0.5, 1, 0],
    ],
    [0, 0, 1],
  );

  if (mode === 'cross') {
    // Plane B: width along Z, at X=0 (rotated 90 degrees about Y from plane A).
    addQuad(
      [
        [0, 0, -0.5],
        [0, 0, 0.5],
        [0, 1, 0.5],
        [0, 1, -0.5],
      ],
      [1, 0, 0],
    );
  }

  return { positions, normals, uvs, indices };
}

/* ------------------------------------------------------------------------- *
 * OSM woodland-polygon planting (issue #56).
 *
 * The primary vegetation source: individual trees are seed-sampled INSIDE the
 * real OSM landcover polygons of data/config/landcover.json (named plantations
 * read as forest; open fields stay open), replacing the roadside-patch guess
 * above. All maths here is pure and three.js-free — vegetation.ts realises the
 * near-camera instances and vegetationCanopy.ts the distant canopy surface.
 *
 * Determinism (hard requirement): a polygon's planting is a seeded jittered
 * grid (the same Math.sin hash01 idiom as the rest of the layer), so a given
 * AOI is byte-identical on every load and identical whether a polygon is
 * realised near the camera or streamed back in later.
 * ------------------------------------------------------------------------- */

/** The three area-fill classes (tree_row is a line, handled separately). */
export type AreaClass = Extract<LandcoverClass, 'forest' | 'wood' | 'scrub'>;

export interface RingBounds {
  minX: number;
  minZ: number;
  maxX: number;
  maxZ: number;
}

/** Converts a BNG [easting, northing] geometry array to local viewer x/z via
 * bngToLocal (the single source of truth for scene placement). */
export function landcoverRingToLocal(geometryBng: [number, number][]): LocalXZ[] {
  return geometryBng.map(([e, n]) => bngToLocal(e, n));
}

/** Axis-aligned bounds of a ring/polyline in local x/z. */
export function ringBounds(ring: LocalXZ[]): RingBounds {
  let minX = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxZ = -Infinity;
  for (const p of ring) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.z < minZ) minZ = p.z;
    if (p.z > maxZ) maxZ = p.z;
  }
  return { minX, minZ, maxX, maxZ };
}

/** Vertex-average centroid (a fine representative point for coarse ground
 * sampling on a woodland polygon; not a true area centroid). */
export function ringCentroidXZ(ring: LocalXZ[]): LocalXZ {
  let sx = 0;
  let sz = 0;
  let n = 0;
  const closed = ring.length > 1 && ring[0].x === ring[ring.length - 1].x && ring[0].z === ring[ring.length - 1].z;
  const last = closed ? ring.length - 1 : ring.length;
  for (let i = 0; i < last; i++) {
    sx += ring[i].x;
    sz += ring[i].z;
    n += 1;
  }
  return n > 0 ? { x: sx / n, z: sz / n } : { x: 0, z: 0 };
}

/**
 * Standard even-odd ray-casting point-in-polygon test in the x/z plane. The
 * ring may be given open or closed (first == last); the wrap edge is always
 * tested. Boundary behaviour is not defined precisely (points exactly on an
 * edge may fall either way) — fine for planting, where a sub-metre boundary
 * decision is immaterial.
 */
export function pointInPolygon(pt: LocalXZ, ring: LocalXZ[]): boolean {
  const closed = ring.length > 1 && ring[0].x === ring[ring.length - 1].x && ring[0].z === ring[ring.length - 1].z;
  const n = closed ? ring.length - 1 : ring.length;
  if (n < 3) return false;
  let inside = false;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = ring[i].x;
    const zi = ring[i].z;
    const xj = ring[j].x;
    const zj = ring[j].z;
    const intersects = zi > pt.z !== zj > pt.z && pt.x < ((xj - xi) * (pt.z - zi)) / (zj - zi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

/** Per-class planting parameters for the near-camera instanced trees. */
interface AreaPlanting {
  kind: VegetationKind;
  /** Base grid spacing (m) between candidate trees at densityScale 1 — smaller
   * = denser. Forest (plantation) is dense; open wood is a sparse scatter. */
  spacingM: number;
  /** Per-slot keep probability (looseness) so a plantation still breathes. */
  keepProb: number;
  /** Card height range (m). */
  heightRange: [number, number];
  /** Variant weights within the kind's palette (see vegetationTexture atlas):
   * tree variant 0 = round broadleaf, 1 = conifer, 2 = sapling. */
  variantWeights: number[];
}

const AREA_PLANTING: Record<AreaClass, AreaPlanting> = {
  // Dense conifer plantation look: tight grid, conifer-heavy, tall.
  forest: { kind: 'tree', spacingM: 8, keepProb: 0.9, heightRange: [9, 17], variantWeights: [0.18, 0.72, 0.1] },
  // Sparser broadleaf scatter.
  wood: { kind: 'tree', spacingM: 14, keepProb: 0.72, heightRange: [7, 13], variantWeights: [0.55, 0.18, 0.27] },
  // Low shrubs.
  scrub: { kind: 'shrub', spacingM: 6, keepProb: 0.8, heightRange: [0.6, 1.6], variantWeights: [0.5, 0.5] },
};

const TWO_PI_ = Math.PI * 2;

/** Upper bound on candidate grid cells generated for a single area polygon,
 * regardless of its size (see sampleAreaInstances). */
const MAX_AREA_CANDIDATES = 6000;

function weightedVariant(weights: number[], r: number): number {
  const total = weights.reduce((a, b) => a + b, 0);
  let acc = 0;
  const target = r * total;
  for (let i = 0; i < weights.length; i++) {
    acc += weights[i];
    if (target < acc) return i;
  }
  return weights.length - 1;
}

export interface AreaSampleOptions {
  /** Scales planting density (issue #17 low tier: fewer instances). The grid
   * step grows as 1/sqrt(densityScale), so instance count scales ~linearly
   * with densityScale. Default 1. */
  densityScale?: number;
  /** Carriageway clearance (issue #79): drops any candidate too close to the
   * route ribbon or a tarmac road centreline (e.g. a road running through the
   * wood). Pure per-candidate filter — does not alter seeds. */
  clearance?: TarmacClearance;
}

/**
 * Seed-samples individual tree/shrub instances on a jittered grid clipped to
 * the interior of one landcover polygon (local x/z). Deterministic in
 * (ring, cls, seed, options): identical every call, so a polygon plants the
 * same trees whether realised now or streamed back later. The y elevation is
 * NOT set here — vegetation.ts grounds each instance on the DTM collider
 * (hard constraint #6: never fabricate ground).
 */
export function sampleAreaInstances(
  ring: LocalXZ[],
  cls: AreaClass,
  seed: number,
  options: AreaSampleOptions = {},
): VegetationInstance[] {
  const cfg = AREA_PLANTING[cls];
  const density = Math.max(1e-3, options.densityScale ?? 1);
  const b = ringBounds(ring);
  let step = cfg.spacingM / Math.sqrt(density);
  // Safety clamp for pathologically large polygons: a single giant woodland
  // must never generate an unbounded candidate grid (the global live cap in
  // vegetation.ts limits what actually renders, but the pure generation itself
  // must stay bounded too). Coarsen the grid so no polygon exceeds
  // MAX_AREA_CANDIDATES cells; big forests simply plant a sparser scatter.
  const bboxArea = Math.max(1, (b.maxX - b.minX) * (b.maxZ - b.minZ));
  const minStepForCap = Math.sqrt(bboxArea / MAX_AREA_CANDIDATES);
  if (minStepForCap > step) step = minStepForCap;
  const nx = Math.max(1, Math.ceil((b.maxX - b.minX) / step));
  const nz = Math.max(1, Math.ceil((b.maxZ - b.minZ) / step));
  const [hMin, hMax] = cfg.heightRange;
  const out: VegetationInstance[] = [];

  for (let gi = 0; gi < nx; gi++) {
    const cellX = b.minX + (gi + 0.5) * step;
    for (let gj = 0; gj < nz; gj++) {
      const cellZ = b.minZ + (gj + 0.5) * step;
      // A stable per-cell seed: integer-mixed with the polygon seed, then run
      // through hash01 for each independent random draw.
      const s = seed * 1000003 + gi * 3907 + gj * 91;
      if (hash01(s * 3.7) >= cfg.keepProb) continue;
      const px = cellX + (hash01(s * 1.1) - 0.5) * step * 0.9;
      const pz = cellZ + (hash01(s * 2.3) - 0.5) * step * 0.9;
      if (!pointInPolygon({ x: px, z: pz }, ring)) continue;
      if (options.clearance && !options.clearance.isClear({ x: px, z: pz })) continue;
      const scale = hMin + hash01(s * 5.1) * (hMax - hMin);
      const variant = weightedVariant(cfg.variantWeights, hash01(s * 8.9));
      const rotationY = hash01(s * 12.3) * TWO_PI_;
      out.push({ x: px, z: pz, kind: cfg.kind, variant, scale, rotationY });
    }
  }
  return out;
}

/** Spacing (m) between trees along a tree_row / hedgerow line at densityScale 1. */
const TREE_ROW_SPACING_M = 9;
const TREE_ROW_HEIGHT_RANGE: [number, number] = [5, 11];
/** Small lateral jitter off the line (m) so a shelterbelt isn't a ruler line. */
const TREE_ROW_JITTER_M = 2.5;

/**
 * Seed-places a line of trees along an OPEN polyline (a tree_row / hedgerow /
 * shelterbelt), reusing the densify/perpendiculars verge idiom shared with
 * roadWalls. Deterministic in (line, seed, options). Trees stay within a few
 * metres of the line (hedgerow, not a scatter).
 */
export function treeRowInstances(line: LocalXZ[], seed: number, options: AreaSampleOptions = {}): VegetationInstance[] {
  if (line.length < 2) return [];
  const density = Math.max(1e-3, options.densityScale ?? 1);
  const step = TREE_ROW_SPACING_M / Math.sqrt(density);
  const pts = densify(line, step);
  const perp = perpendiculars(pts);
  const [hMin, hMax] = TREE_ROW_HEIGHT_RANGE;
  const out: VegetationInstance[] = [];
  for (let i = 0; i < pts.length; i++) {
    const s = seed * 7649 + i * 131 + 1;
    if (hash01(s * 2.9) >= 0.85) continue; // occasional gap
    const off = (hash01(s * 4.1) - 0.5) * 2 * TREE_ROW_JITTER_M;
    const px = pts[i].x + perp[i].x * off;
    const pz = pts[i].z + perp[i].z * off;
    if (options.clearance && !options.clearance.isClear({ x: px, z: pz })) continue;
    const scale = hMin + hash01(s * 5.1) * (hMax - hMin);
    const variant = Math.floor(hash01(s * 8.9) * TREE_VARIANTS);
    const rotationY = hash01(s * 12.3) * TWO_PI_;
    out.push({ x: px, z: pz, kind: 'tree', variant, scale, rotationY });
  }
  return out;
}

export interface CanopyClassStyle {
  /** Treetop height (m) the canopy surface floats above sampled ground. */
  heightM: number;
  /** Flat RGB tint (each channel in [0,1]) for the overview canopy fill. */
  color: [number, number, number];
}

// Brighter, clearly-green tints (was near-black conifer green): the overview
// canopy is DoubleSide and the scene's ambient fill is weak, so a dark tint
// read as a black ceiling on any face the sun didn't hit. These greens plus the
// small emissive floor in vegetationCanopy.ts keep it reading as woodland from
// above and at grazing angles, never black, while staying clearly stylised.
const CANOPY_STYLE: Record<AreaClass, CanopyClassStyle> = {
  forest: { heightM: 12, color: [0.22, 0.42, 0.24] }, // conifer green
  wood: { heightM: 9.5, color: [0.32, 0.5, 0.28] }, // mid broadleaf green
  scrub: { heightM: 1.6, color: [0.44, 0.5, 0.3] }, // low olive
};

/** Overview canopy style for an area class, or null for tree_row (a line, so
 * no fill surface — realised only as instances). */
export function classCanopy(cls: LandcoverClass): CanopyClassStyle | null {
  return cls === 'tree_row' ? null : CANOPY_STYLE[cls];
}

function signedAreaXZ(pts: LocalXZ[]): number {
  let a = 0;
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i];
    const q = pts[(i + 1) % pts.length];
    a += p.x * q.z - q.x * p.z;
  }
  return a / 2;
}

function triCross(a: LocalXZ, b: LocalXZ, c: LocalXZ): number {
  return (b.x - a.x) * (c.z - a.z) - (b.z - a.z) * (c.x - a.x);
}

function pointInTri(p: LocalXZ, a: LocalXZ, b: LocalXZ, c: LocalXZ): boolean {
  const d1 = triCross(p, a, b);
  const d2 = triCross(p, b, c);
  const d3 = triCross(p, c, a);
  const hasNeg = d1 < 0 || d2 < 0 || d3 < 0;
  const hasPos = d1 > 0 || d2 > 0 || d3 > 0;
  return !(hasNeg && hasPos);
}

/**
 * Ear-clipping triangulation of a simple polygon in the x/z plane. Returns a
 * flat list of vertex-index triples into the polygon's vertices (the closing
 * duplicate point, if present, is dropped, so indices reference the first
 * `n` distinct vertices). For a simple polygon this yields exactly n-2
 * triangles. A non-simple / self-touching polygon that stalls the algorithm
 * bails early with whatever fan it could clip (the overview canopy simply
 * misses that scrap of fill rather than crashing).
 */
export function triangulateXZ(ring: LocalXZ[]): number[] {
  const pts = ring.slice();
  if (pts.length > 1 && pts[0].x === pts[pts.length - 1].x && pts[0].z === pts[pts.length - 1].z) pts.pop();
  const n = pts.length;
  if (n < 3) return [];

  const order = [...Array(n).keys()];
  if (signedAreaXZ(pts) < 0) order.reverse(); // work CCW

  const result: number[] = [];
  let remaining = order.slice();
  let guard = 0;
  const maxIters = n * n;
  while (remaining.length > 3 && guard < maxIters) {
    guard += 1;
    let clipped = false;
    for (let i = 0; i < remaining.length; i++) {
      const ia = remaining[(i - 1 + remaining.length) % remaining.length];
      const ib = remaining[i];
      const ic = remaining[(i + 1) % remaining.length];
      const a = pts[ia];
      const b = pts[ib];
      const c = pts[ic];
      if (triCross(a, b, c) <= 0) continue; // reflex or degenerate: not an ear
      let containsOther = false;
      for (const k of remaining) {
        if (k === ia || k === ib || k === ic) continue;
        if (pointInTri(pts[k], a, b, c)) {
          containsOther = true;
          break;
        }
      }
      if (containsOther) continue;
      result.push(ia, ib, ic);
      remaining.splice(i, 1);
      clipped = true;
      break;
    }
    if (!clipped) break; // non-simple polygon: stop
  }
  if (remaining.length === 3) result.push(remaining[0], remaining[1], remaining[2]);
  return result;
}

/**
 * Distance-decimates a ring so consecutive kept vertices are at least
 * minSpacingM apart, collapsing the densely-sampled OSM woodland boundaries
 * (many vertices metres apart) that would otherwise dominate the canopy's
 * ground-raycast budget (issue #17). Every kept vertex is still grounded on a
 * real DTM sample — decimation drops vertices, it never invents height. Shape
 * is preserved to ~minSpacingM; a ring that decimates below 3 vertices is
 * returned unchanged (too small to simplify safely). Pure and three.js-free.
 */
export function simplifyRing(ring: LocalXZ[], minSpacingM: number): LocalXZ[] {
  const closed = ring.length > 1 && ring[0].x === ring[ring.length - 1].x && ring[0].z === ring[ring.length - 1].z;
  const src = closed ? ring.slice(0, -1) : ring;
  if (src.length <= 4 || minSpacingM <= 0) return ring;
  const min2 = minSpacingM * minSpacingM;
  const kept: LocalXZ[] = [src[0]];
  for (let i = 1; i < src.length; i += 1) {
    const last = kept[kept.length - 1];
    const dx = src[i].x - last.x;
    const dz = src[i].z - last.z;
    if (dx * dx + dz * dz >= min2) kept.push(src[i]);
  }
  // Drop a final vertex that sits right on top of the first (the wrap edge is
  // implicit) so we don't emit a zero-length closing edge.
  if (kept.length > 3) {
    const first = kept[0];
    const lastKept = kept[kept.length - 1];
    const dx = lastKept.x - first.x;
    const dz = lastKept.z - first.z;
    if (dx * dx + dz * dz < min2) kept.pop();
  }
  return kept.length >= 3 ? kept : ring;
}

export interface DrapeMesh {
  /** Draped vertices (local x/z). Each still needs its OWN ground y sample —
   * that is the whole point: the canopy follows the terrain contour rather than
   * floating as one flat sheet. */
  points: LocalXZ[];
  /** Triangle vertex-index triples into `points`. */
  tris: number[];
}

/**
 * Subdivides a triangulated polygon so no triangle edge is much longer than
 * targetEdgeM, so the overview canopy built on top can DRAPE over the terrain
 * (each vertex grounded on its own DTM sample) instead of being one flat sheet
 * floated at a single centroid height. Uniform midpoint (1->4) subdivision with
 * a shared edge-midpoint cache, so two triangles sharing an edge split it
 * identically — no T-junction cracks. The subdivision level comes from the
 * polygon's longest edge but is capped so a single (possibly huge) woodland can
 * never exceed maxTris triangles: a giant polygon drapes coarsely rather than
 * exploding the vertex / ground-raycast budget (issue #17).
 *
 * Pure and three.js-free. The y elevation is NEVER invented here (hard
 * constraint #6) — vegetationCanopy.ts grounds every returned point on the same
 * DTM collider that backs walk-mode ground lock.
 */
export function drapePolygon(
  verts: LocalXZ[],
  tris: number[],
  targetEdgeM: number,
  maxTris: number,
): DrapeMesh {
  if (tris.length === 0) return { points: [], tris: [] };
  const points: LocalXZ[] = verts.map((v) => ({ x: v.x, z: v.z }));
  let faces = tris.slice();

  // Longest base-triangle edge decides how many times to halve edges.
  let maxEdge2 = 0;
  for (let f = 0; f < faces.length; f += 3) {
    const a = points[faces[f]];
    const b = points[faces[f + 1]];
    const c = points[faces[f + 2]];
    const e0 = (a.x - b.x) ** 2 + (a.z - b.z) ** 2;
    const e1 = (b.x - c.x) ** 2 + (b.z - c.z) ** 2;
    const e2 = (c.x - a.x) ** 2 + (c.z - a.z) ** 2;
    maxEdge2 = Math.max(maxEdge2, e0, e1, e2);
  }
  const maxEdge = Math.sqrt(maxEdge2);
  let level = targetEdgeM > 0 && maxEdge > targetEdgeM ? Math.ceil(Math.log2(maxEdge / targetEdgeM)) : 0;
  const baseTriCount = faces.length / 3;
  while (level > 0 && baseTriCount * 4 ** level > maxTris) level -= 1;
  if (level <= 0) return { points, tris: faces };

  const midCache = new Map<string, number>();
  const midpoint = (i: number, j: number): number => {
    const key = i < j ? `${i}:${j}` : `${j}:${i}`;
    const hit = midCache.get(key);
    if (hit !== undefined) return hit;
    const pa = points[i];
    const pb = points[j];
    const idx = points.length;
    points.push({ x: (pa.x + pb.x) / 2, z: (pa.z + pb.z) / 2 });
    midCache.set(key, idx);
    return idx;
  };

  for (let l = 0; l < level; l += 1) {
    const next: number[] = [];
    for (let f = 0; f < faces.length; f += 3) {
      const i0 = faces[f];
      const i1 = faces[f + 1];
      const i2 = faces[f + 2];
      const m01 = midpoint(i0, i1);
      const m12 = midpoint(i1, i2);
      const m20 = midpoint(i2, i0);
      next.push(i0, m01, m20, m01, i1, m12, m20, m12, i2, m01, m12, m20);
    }
    faces = next;
  }
  return { points, tris: faces };
}

/**
 * Crossfade weight for an overview-canopy vertex at horizontal distance distM
 * from the camera. 0 within nearRadiusM — there the near-camera instanced trees
 * ARE the canopy, so drawing the sheet as well would put a floating "ceiling"
 * over the real trees — ramping smoothly (smoothstep) to 1 by
 * nearRadiusM + fadeBandM, the distant overview regime. Keeps the transition
 * from popping as the camera moves. Purely cosmetic (hard constraint #1).
 */
export function canopyFadeAlpha(distM: number, nearRadiusM: number, fadeBandM: number): number {
  if (fadeBandM <= 0) return distM <= nearRadiusM ? 0 : 1;
  const t = (distM - nearRadiusM) / fadeBandM;
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  return t * t * (3 - 2 * t);
}
